'use client'

import Link from 'next/link'
import { MarketingLayout } from '@/components/marketing/MarketingLayout'

type UseCase = {
  icon: string
  title: string
  subtitle: string
  intro?: { text: string; accent?: boolean }[]
  cases: { challenge: string; description: string; insight: string }[]
}

const USE_CASES: UseCase[] = [
  {
    icon: '🏛️',
    title: 'Government & Policy',
    subtitle: 'Understand which communities will adopt new programs—and which will resist.',
    cases: [
      {
        challenge: 'Programs that fail to reach the people who need them',
        description: 'Social programs often show high uptake in some communities and near-zero in others—even with identical outreach.',
        insight: 'Low uptake looks like an awareness problem—but it really shows the need to match the type of trust to the challenge at hand.',
      },
      {
        challenge: 'Public meetings that don\'t represent the public',
        description: 'Town halls attract the usual voices. The people most affected by policy changes often don\'t show up.',
        insight: 'There\'s a difference between trusting a food bank run by a church and reporting a pothole outside your house. One relies on inward (bonding) trust, the other on outward (bridging) trust. That difference is everything.',
      },
    ],
  },
  {
    icon: '🏥',
    title: 'Healthcare & Public Health',
    subtitle: 'Design interventions that communities will actually adopt—not just tolerate.',
    intro: [
      { text: 'We searched a long time for the thing that unifies the work—the single idea beneath the substance-abuse engagement, the maternal-health engagement, the chronic-care engagement, and the care of elders and children.' },
      { text: 'Not a magical elixir. The most common substance there is—and the one everything else runs on.', accent: true },
    ],
    cases: [
      {
        challenge: 'Health campaigns that don\'t change behavior',
        description: 'What makes it powerful is not that it is exotic but that it is everywhere, that almost no one measures it, and that when it is present a message lands and when it is absent the same message dies. We did not invent trust. We learned to read it.',
        insight: '"This community\'s low rate isn\'t hesitancy—it\'s institutional distrust. The health department is the wrong messenger. Partner with churches that function as trust anchors."',
      },
      {
        challenge: 'Facility siting that triggers backlash',
        description: 'New clinics and care facilities face unexpected opposition—sometimes from the very communities they\'re meant to serve.',
        insight: 'Opposition isn\'t about the facility. It\'s about a process that can land on a community like a bankruptcy notice—where, on the weight of history alone, no benefit ever feels worth the cost.',
      },
    ],
  },
  {
    icon: '🏗️',
    title: 'Real Estate & Development',
    subtitle: 'Predict community response before you break ground—or break trust.',
    cases: [
      {
        challenge: 'Projects blocked at the finish line',
        description: 'Developments that clear every regulatory hurdle can still die to community opposition.',
        insight: '"The adjacent neighborhood shows high place attachment and Guardian-dominant profiles. Start engagement 18 months earlier, through their historical society."',
      },
      {
        challenge: 'Mixed-use projects that don\'t mix',
        description: 'Retail components often underperform—not because of design, but because the surrounding community doesn\'t engage.',
        insight: '"Low bridging trust means residents won\'t cross neighborhood boundaries for amenities. Your retail needs to serve immediate neighbors only."',
      },
    ],
  },
  {
    icon: '📋',
    title: 'Insurance & Risk',
    subtitle: 'See behavioral factors that predict community resilience—and claims.',
    cases: [
      {
        challenge: 'Risk models that miss community dynamics',
        description: 'Two areas with similar physical risk can have very different claim outcomes based on community cohesion.',
        insight: '"Community A has mutual aid networks—claims cluster around infrastructure. Community B shows brittleness—expect higher individual claims."',
      },
      {
        challenge: 'Preparedness program adoption',
        description: 'Risk mitigation programs see low uptake in communities that need them most.',
        insight: '"Low adoption isn\'t apathy—it\'s messenger mismatch. Activate cross-boundary connectors and adoption triples."',
      },
    ],
  },
  {
    icon: '🎓',
    title: 'Education',
    subtitle: 'Find families whose values align with your institution—beyond demographics.',
    cases: [
      {
        challenge: 'Enrollment that doesn\'t stick',
        description: 'Schools invest heavily in recruitment, but many families don\'t persist—because of values confusion, not dissatisfaction.',
        insight: '"Your strongest retention is Bridge Builder profiles. These 12 zip codes over-index for that profile. Shift recruitment spend accordingly."',
      },
      {
        challenge: 'Positioning that attracts the wrong families',
        description: 'Schools that refuse to clarify who they\'re for attract families who leave.',
        insight: '"Your messaging implies structure, but your community values exploration. That mismatch is driving transfers."',
      },
    ],
  },
  {
    icon: '📣',
    title: 'Civic Campaigns',
    subtitle: 'Find the moveable middle and the messengers who can move them.',
    cases: [
      {
        challenge: 'Persuasion spend that doesn\'t persuade',
        description: 'Campaigns target based on demographics and past voting—missing who\'s actually persuadable.',
        insight: '"The 12-18% who decide this race are Bridge Builders who respond to authenticity over ideology. Here\'s where they cluster."',
      },
      {
        challenge: 'Coalitions that fragment under pressure',
        description: 'Coalitions assembled on paper fall apart because they were built on shared positions, not shared values.',
        insight: '"These groups align on this issue but have different stances—Guardian vs. Expander. Under pressure, they\'ll fracture."',
      },
    ],
  },
  {
    icon: '🌿',
    title: 'Environmental & Conservation',
    subtitle: 'Design solutions communities will steward—not just tolerate.',
    cases: [
      {
        challenge: 'Conservation projects that lose support',
        description: 'Restoration initiatives face resistance—not because communities oppose nature, but because they weren\'t brought in correctly.',
        insight: '"Guardian-dominant with high place attachment. Lead with heritage framing, not climate urgency. Partner with the historical society."',
      },
      {
        challenge: 'Brownfield redevelopment stalls',
        description: 'Converting underutilized land requires buy-in that zoning alone can\'t deliver.',
        insight: '"Adjacent neighborhoods have different stances. Sequence engagement with the opportunity-oriented community first."',
      },
    ],
  },
  {
    icon: '💰',
    title: 'Impact Investment',
    subtitle: 'Assess community readiness before capital deployment—not after.',
    cases: [
      {
        challenge: 'Impact investments that don\'t land',
        description: 'Capital deployed without understanding behavioral dynamics underperforms—adoption is harder than expected.',
        insight: '"Low bridging trust means outside capital faces friction. This adjacent market has similar fundamentals but higher trust mobility."',
      },
      {
        challenge: 'Portfolio companies struggling locally',
        description: 'Investments in community-facing businesses stumble on dynamics invisible in diligence.',
        insight: '"Resistance is spillover from a previous development that broke trust. You need a repair strategy before a growth strategy."',
      },
    ],
  },
]

export default function UseCasesPage() {
  return (
    <MarketingLayout>
      <section className="page-header">
        <div className="container">
          <div className="page-header-content">
            <div className="eyebrow">Applications</div>
            <h1>Use Cases</h1>
            <p>Where Community Intelligence makes the difference between initiatives that land and initiatives that miss.</p>
          </div>
        </div>
      </section>

      <section className="use-cases-section">
        <div className="container">
          {USE_CASES.map((useCase) => (
            <div key={useCase.title} className="use-case">
              <div className="use-case-header">
                <div className="use-case-icon">{useCase.icon}</div>
                <div className="use-case-header-text">
                  <h2>{useCase.title}</h2>
                  <p>{useCase.subtitle}</p>
                </div>
              </div>
              {useCase.intro && (
                <div className="use-case-intro">
                  {useCase.intro.map((para, i) => (
                    <p key={i} className={para.accent ? 'intro-accent' : undefined}>{para.text}</p>
                  ))}
                </div>
              )}
              <div className="use-case-content">
                {useCase.cases.map((c, i) => (
                  <div key={i} className="use-case-card">
                    <h3>The challenge</h3>
                    <h4>{c.challenge}</h4>
                    <p>{c.description}</p>
                    <div className="insight">
                      <p className="insight-label">What CI reveals</p>
                      <p>{c.insight}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <div className="cta-card">
            <h2>Which challenge is yours?</h2>
            <p>Tell us what you&apos;re working on. We&apos;ll show you what Community Intelligence can see.</p>
            <Link href="/contact" className="btn-primary">
              Start a Conversation
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
                      </div>
        </div>
      </section>
    </MarketingLayout>
  )
}
